import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Loader2 } from "lucide-react";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";

interface Props { ownerId: string; }
interface Prefs {
  email_on_booking: boolean;
  email_on_cancellation: boolean;
  email_on_review: boolean;
  daily_digest: boolean;
}

const NotificationsTab = ({ ownerId }: Props) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [prefs, setPrefs] = useState<Prefs>({
    email_on_booking: true,
    email_on_cancellation: true,
    email_on_review: true,
    daily_digest: false,
  });

  useEffect(() => {
    const load = async () => {
      const { data } = await supabase
        .from("owner_notification_prefs")
        .select("*")
        .eq("owner_id", ownerId)
        .maybeSingle();
      if (data) setPrefs(data as Prefs);
      setLoading(false);
    };
    load();
  }, [ownerId]);

  const update = async (key: keyof Prefs, value: boolean) => {
    const next = { ...prefs, [key]: value };
    setPrefs(next);
    const { error } = await supabase
      .from("owner_notification_prefs")
      .upsert({ owner_id: ownerId, ...next, updated_at: new Date().toISOString() }, { onConflict: "owner_id" });
    if (error) toast({ title: "Failed to save", variant: "destructive" });
  };

  if (loading) return <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin" /></div>;

  const items: Array<{ key: keyof Prefs; label: string; desc: string }> = [
    { key: "email_on_booking", label: "New booking emails", desc: "Get an email when a driver books" },
    { key: "email_on_cancellation", label: "Cancellation emails", desc: "When a booking is cancelled" },
    { key: "email_on_review", label: "Review emails", desc: "When a driver leaves feedback" },
    { key: "daily_digest", label: "Daily digest", desc: "Once-per-day summary email" },
  ];

  return (
    <Card>
      <CardHeader>
        <CardTitle>Owner notifications</CardTitle>
        <CardDescription>Choose what to be emailed about</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {items.map((i) => (
          <div key={i.key} className="flex items-center justify-between py-2">
            <div>
              <Label className="text-base">{i.label}</Label>
              <p className="text-sm text-muted-foreground">{i.desc}</p>
            </div>
            <Switch checked={prefs[i.key]} onCheckedChange={(v) => update(i.key, v)} />
          </div>
        ))}
      </CardContent>
    </Card>
  );
};

export default NotificationsTab;
